<script>
  import { createEventDispatcher, onDestroy } from 'svelte'
  import {
    formatDayLabel,
    formatPnlPct,
    monthGrid,
    monthOfDay,
    mskToday,
    shiftMonth,
  } from '../lib/tradingDay.js'

  /** Selected trading day YYYY-MM-DD */
  export let value = ''
  /** Today MSK YYYY-MM-DD (from API when available) */
  export let today = ''
  /**
   * Calendar days map from API:
   * { [day]: { pnl_pct?, bet_count?, has_history? } }
   */
  export let days = {}
  /** Visible month YYYY-MM */
  export let month = ''
  export let disabled = false
  export let label = 'День'

  const dispatch = createEventDispatcher()

  let open = false
  /** @type {HTMLElement | null} */
  let root = null
  /** Visible month; owned locally so nav isn't fought by parent prop updates. */
  let viewMonth = ''

  $: todayId = today || mskToday()
  $: selected = value || todayId
  $: grid = monthGrid(viewMonth || monthOfDay(selected))
  $: btnLabel = formatDayLabel(selected, { today: todayId })

  // Seed once from props / selection.
  $: if (!viewMonth) viewMonth = month || monthOfDay(selected)

  function bit(day) {
    return (days && days[day]) || null
  }

  function pctClass(day) {
    const b = bit(day)
    if (!b || b.pnl_pct == null || !Number.isFinite(Number(b.pnl_pct))) return ''
    const v = Number(b.pnl_pct)
    if (v > 0.05) return 'is-up'
    if (v < -0.05) return 'is-down'
    return 'is-flat'
  }

  function select(day) {
    if (!day || disabled) return
    if (day > todayId) return
    open = false
    viewMonth = monthOfDay(day)
    if (day === selected) return
    value = day
    dispatch('change', { day, month: monthOfDay(day) })
  }

  function toggle() {
    if (disabled) return
    open = !open
  }

  function prevMonth() {
    viewMonth = shiftMonth(viewMonth || monthOfDay(selected), -1)
    dispatch('month', { month: viewMonth })
  }

  function nextMonth() {
    const base = viewMonth || monthOfDay(selected)
    const next = shiftMonth(base, 1)
    if (next > monthOfDay(todayId)) return
    viewMonth = next
    dispatch('month', { month: viewMonth })
  }

  function onDoc(e) {
    if (!open || !root) return
    if (e.target instanceof Node && !root.contains(e.target)) open = false
  }

  function onKey(e) {
    if (e.key === 'Escape') open = false
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', onDoc)
    document.addEventListener('keydown', onKey)
  }

  onDestroy(() => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('pointerdown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  })
</script>

<div class="pm-day" bind:this={root} data-open={open ? '1' : '0'}>
  <button
    type="button"
    class="pm-day__btn"
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-label={label}
    {disabled}
    on:click={toggle}
  >
    <span class="pm-day__label">{btnLabel}</span>
    <span class="pm-day__chev" aria-hidden="true">▾</span>
  </button>

  {#if open}
    <div class="pm-day__pop" role="dialog" aria-label="Календарь торгового дня MSK">
      <header class="pm-day__head">
        <button type="button" class="pm-day__nav" aria-label="Предыдущий месяц" on:click={prevMonth}
          >‹</button
        >
        <span class="pm-day__month">{grid.label}</span>
        <button
          type="button"
          class="pm-day__nav"
          aria-label="Следующий месяц"
          disabled={viewMonth >= monthOfDay(todayId)}
          on:click={nextMonth}>›</button
        >
      </header>
      <div class="pm-day__dow" aria-hidden="true">
        {#each ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'] as w}
          <span>{w}</span>
        {/each}
      </div>
      <div class="pm-day__grid">
        {#each grid.weeks as week}
          {#each week as cell}
            {#if !cell.day}
              <span class="pm-day__cell is-empty"></span>
            {:else}
              {@const b = bit(cell.day)}
              {@const future = cell.day > todayId}
              <button
                type="button"
                class="pm-day__cell"
                class:is-on={cell.day === selected}
                class:is-today={cell.day === todayId}
                class:is-hist={b && b.has_history}
                class:is-future={future}
                disabled={future}
                title={b && b.pnl_pct != null
                  ? `${cell.day} · ${formatPnlPct(b.pnl_pct)} от B_day`
                  : cell.day}
                on:click={() => select(cell.day)}
              >
                <span class="pm-day__dom">{cell.dom}</span>
                <span class="pm-day__pct {pctClass(cell.day)}">
                  {b && b.has_history ? formatPnlPct(b.pnl_pct) || '·' : ''}
                </span>
              </button>
            {/if}
          {/each}
        {/each}
      </div>
      <p class="pm-day__foot">день MSK · % к старту дня</p>
    </div>
  {/if}
</div>

<style>
  .pm-day {
    position: relative;
    display: inline-block;
  }
  .pm-day__btn {
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin: 0;
    padding: 0.28rem 0.65rem;
    border-radius: 999px;
    border: 1px solid var(--pm-glass-border);
    background: var(--pm-glass);
    color: var(--pm-ink);
    font: inherit;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  }
  .pm-day__btn:hover:not(:disabled) {
    border-color: rgba(var(--pm-ink-rgb), 0.28);
    background: var(--pm-glass-strong);
  }
  .pm-day__btn:disabled {
    opacity: 0.55;
    cursor: wait;
  }
  .pm-day__chev {
    font-size: 0.65rem;
    color: var(--pm-ink-mute);
    transform: translateY(-0.05em);
  }
  .pm-day[data-open='1'] .pm-day__chev {
    transform: rotate(180deg) translateY(0.05em);
  }
  .pm-day__pop {
    position: absolute;
    top: calc(100% + 0.45rem);
    right: 0;
    z-index: 40;
    min-width: min(22.5rem, calc(100vw - 1.5rem));
    width: min(24rem, calc(100vw - 1.5rem));
    max-width: calc(100vw - 1.25rem);
    padding: 0.9rem 0.95rem 0.75rem;
    border-radius: 1rem;
    border: 1px solid var(--pm-glass-border);
    background: var(--pm-glass-strong);
    box-shadow: 0 18px 40px -18px rgba(var(--pm-ink-rgb), 0.45);
    backdrop-filter: blur(12px);
    animation: pm-day-in 0.16s var(--pm-ease-out, ease-out);
  }
  @keyframes pm-day-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  .pm-day__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.45rem;
    margin-bottom: 0.6rem;
  }
  .pm-day__month {
    font-size: 0.92rem;
    font-weight: 650;
    letter-spacing: 0.02em;
    color: var(--pm-ink);
  }
  .pm-day__nav {
    appearance: none;
    width: 2.05rem;
    height: 2.05rem;
    border-radius: 999px;
    border: 1px solid var(--pm-glass-border);
    background: var(--pm-glass);
    color: var(--pm-ink-soft);
    font: inherit;
    font-size: 1.15rem;
    line-height: 1;
    cursor: pointer;
  }
  .pm-day__nav:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .pm-day__dow {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.28rem;
    margin-bottom: 0.3rem;
  }
  .pm-day__dow span {
    text-align: center;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .pm-day__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.28rem;
  }
  .pm-day__cell {
    appearance: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.18rem;
    min-height: 2.85rem;
    margin: 0;
    padding: 0.28rem 0.12rem;
    border-radius: 0.55rem;
    border: 1px solid transparent;
    background: transparent;
    color: var(--pm-ink);
    font: inherit;
    cursor: pointer;
  }
  .pm-day__cell.is-empty {
    pointer-events: none;
  }
  .pm-day__cell.is-hist {
    background: rgba(var(--pm-ink-rgb), 0.04);
  }
  .pm-day__cell.is-today {
    border-color: rgba(var(--pm-ink-rgb), 0.18);
  }
  .pm-day__cell.is-on {
    border-color: rgba(var(--pm-ink-rgb), 0.35);
    background: var(--pm-glass);
    box-shadow: inset 0 0 0 1px rgba(var(--pm-ink-rgb), 0.06);
  }
  .pm-day__cell.is-future {
    opacity: 0.28;
    cursor: default;
  }
  .pm-day__cell:not(.is-future):hover {
    background: rgba(var(--pm-ink-rgb), 0.07);
  }
  .pm-day__dom {
    font-size: 0.92rem;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .pm-day__pct {
    min-height: 0.9rem;
    font-family: var(--pm-mono);
    font-size: 0.68rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    color: var(--pm-ink-mute);
    line-height: 1;
  }
  .pm-day__pct.is-up {
    color: var(--pm-ok);
  }
  .pm-day__pct.is-down {
    color: var(--pm-bad);
  }
  .pm-day__foot {
    margin: 0.6rem 0 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
    text-align: center;
  }
  @media (max-width: 380px) {
    .pm-day__pop {
      min-width: 0;
      width: calc(100vw - 1.25rem);
      padding: 0.75rem 0.7rem 0.65rem;
    }
    .pm-day__cell {
      min-height: 2.55rem;
      padding: 0.2rem 0.06rem;
    }
    .pm-day__dom {
      font-size: 0.84rem;
    }
    .pm-day__pct {
      font-size: 0.6rem;
    }
  }
</style>
